/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";

import { useLayout } from "@/../_metronic/layout/core";
import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import {
	getResumeIndicatorB5,
	ResumeIndicatorB5Data,
} from "@/services/esus/indicator/esb/b5/getResumeIndicatorB5";
import { Paginated } from "@/@types/generics/paginated";
import { useDebounce } from "@/hooks/useDebounce";
import {
	getResumeIndicatorB5ByHealthTeam,
	ResumeIndicatorB5ByHealthTeamData,
} from "@/services/esus/indicator/esb/b5/getResumeIndicatorB5ByHealthTeam";

import { IndicatorEsbB5Resume } from "./IndicatorEsbB5Resume";
import { IndicatorEsbB5HealthTeams } from "./IndicatorEsbB5HealthTeams";
import { IndicatorEsbB5Citizens } from "./IndicatorEsbB5Citizens";
import {
	getResumeCitizensProceduresIndicatorB5,
	ResumeCitizenProceduresIndicatorB5Data,
} from "@/services/esus/indicator/esb/b5/getResumeCitizensProceduresIndicatorB5";

type IndicatorEsbB5Props = {
	selectedQuarter: string;
	selectedYear: number;
	selectedMonth: number;
	selectedIne?: string;
	isPendingToUpdate: boolean;
	setIsPendingToUpdate: (isPendingToUpdate: boolean) => void;
};

export function IndicatorEsbB5({
	selectedQuarter,
	selectedYear,
	selectedMonth,
	selectedIne,
	isPendingToUpdate,
	setIsPendingToUpdate,
}: Readonly<IndicatorEsbB5Props>) {
	const { setTitle } = useLayout();
	const { uf, ibgeCode } = useCurrentAccount();
	const pageSize = 10;
	const [resumeIndicatorB5, setResumeIndicatorB5] = useState<ResumeIndicatorB5Data>(
		new ResumeIndicatorB5Data()
	);

	const [pageNumberHealthTeamsClassifications, setPageNumberHealthTeamsClassifications] =
		useState(1);
	const [searchHealthTeamsClassification, setSearchHealthTeamsClassification] = useState("");
	const debouncedSearchHealthTeamsClassifications = useDebounce(
		searchHealthTeamsClassification,
		2000
	);
	const [paginatedHealthTeamsClassifications, setPaginatedHealthTeamsClassifications] = useState(
		new Paginated<ResumeIndicatorB5ByHealthTeamData>()
	);
	const [isLoadingHealthTeamsClassifications, setIsLoadingHealthTeamsClassifications] =
		useState(false);

	const [pageNumberCitizens, setPageNumberCitizens] = useState(1);
	const [searchCitizens, setSearchCitizens] = useState("");
	const debouncedSearchCitizens = useDebounce(searchCitizens, 2000);
	const [isEvaluatedCitizens, setIsEvaluatedCitizens] = useState<boolean | undefined>(true);
	const [paginatedCitizens, setPaginatedCitizens] = useState(
		new Paginated<ResumeCitizenProceduresIndicatorB5Data>()
	);
	const [isLoadingCitizens, setIsLoadingCitizens] = useState(false);

	async function handleFilter() {
		await Promise.all([
			handleGetResumeIndicatorB5(),
			handleGetResumeIndicatorB5ByHealthTeam(),
			handleGetResumeCitizensProceduresIndicatorB5(),
		]);
		setIsPendingToUpdate(false);
	}

	async function handleGetResumeIndicatorB5() {
		setResumeIndicatorB5(
			await getResumeIndicatorB5({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
			})
		);
	}

	async function handleGetResumeIndicatorB5ByHealthTeam(pageNumber?: number) {
		setIsLoadingHealthTeamsClassifications(true);
		setPaginatedHealthTeamsClassifications(
			await getResumeIndicatorB5ByHealthTeam({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchHealthTeamsClassification,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingHealthTeamsClassifications(false);
	}

	async function handleGetAllResumeIndicatorB5ByHealthTeam() {
		return (
			await getResumeIndicatorB5ByHealthTeam({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchHealthTeamsClassification,
				pageNumber: 1,
				pageSize: paginatedHealthTeamsClassifications.totalRecords,
				export: true,
			})
		).data;
	}

	async function handleGetResumeCitizensProceduresIndicatorB5(pageNumber?: number) {
		setIsLoadingCitizens(true);
		setPaginatedCitizens(
			await getResumeCitizensProceduresIndicatorB5({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizens,
				isEvaluated: isEvaluatedCitizens,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingCitizens(false);
	}

	async function handleGetAllResumeCitizensProceduresIndicatorB5() {
		return (
			await getResumeCitizensProceduresIndicatorB5({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizens,
				isEvaluated: isEvaluatedCitizens,
				pageNumber: 1,
				pageSize: paginatedCitizens.totalRecords,
				export: true,
			})
		).data;
	}

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetResumeIndicatorB5ByHealthTeam(pageNumberHealthTeamsClassifications);
	}, [pageNumberHealthTeamsClassifications]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberHealthTeamsClassifications === 1) {
				handleGetResumeIndicatorB5ByHealthTeam(pageNumberHealthTeamsClassifications);
			} else {
				setPageNumberHealthTeamsClassifications(1);
			}
		}
	}, [debouncedSearchHealthTeamsClassifications]);

	useEffect(() => {
		if (!isPendingToUpdate) handleGetResumeCitizensProceduresIndicatorB5(pageNumberCitizens);
	}, [pageNumberCitizens]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberCitizens === 1) {
				handleGetResumeCitizensProceduresIndicatorB5(pageNumberCitizens);
			} else {
				setPageNumberCitizens(1);
			}
		}
	}, [debouncedSearchCitizens, isEvaluatedCitizens]);

	useEffect(() => {
		if (isPendingToUpdate) handleFilter();
	}, [isPendingToUpdate]);

	useEffect(() => {
		setTitle("B5 - Procedimentos Odontológicos Preventivos");
	}, [setTitle]);

	return (
		<>
			<IndicatorEsbB5Resume resume={resumeIndicatorB5} />
			<IndicatorEsbB5HealthTeams
				paginatedHealthTeamsClassifications={paginatedHealthTeamsClassifications}
				isLoading={isLoadingHealthTeamsClassifications}
				search={searchHealthTeamsClassification}
				setSearch={setSearchHealthTeamsClassification}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberHealthTeamsClassifications}
				setPageNumber={setPageNumberHealthTeamsClassifications}
				handleGetAllResumeIndicatorB5ByHealthTeam={
					handleGetAllResumeIndicatorB5ByHealthTeam
				}
			/>
			<IndicatorEsbB5Citizens
				paginatedCitizens={paginatedCitizens}
				isLoading={isLoadingCitizens}
				search={searchCitizens}
				setSearch={setSearchCitizens}
				isEvaluated={isEvaluatedCitizens}
				setIsEvaluated={setIsEvaluatedCitizens}
				selectedQuarter={selectedQuarter}
				selectedYear={selectedYear}
				selectedMonth={selectedMonth}
				selectedIne={selectedIne}
				pageNumber={pageNumberCitizens}
				setPageNumber={setPageNumberCitizens}
				handleGetAllResumeCitizensProceduresIndicatorB5={
					handleGetAllResumeCitizensProceduresIndicatorB5
				}
			/>
		</>
	);
}
